import { findIsanDistrict, isanPlace } from './isan-places.ts';
import { venueMapUrl, type VenueSetting } from './venue.ts';

/**
 * `{ระยะทางมาสนาม}` (owner, 22 ต.ค. 2569: "ส่งข้อมูล ระยะทาง+เวลา จัดข้อความอย่างเหมาะสมเข้าใจง่าย เตรียมไว้เพื่อส่งหา
 * ลูกค้า") — how far the customer's district is from the field and roughly how long the drive takes.
 *
 * Filled the same way by the bot (server) and by saved replies (web). The place comes only from the teams the customer
 * registered for match days ahead (the team's province/district). The figures
 * are the stored driving route (`venue_travel`, worked out by the worker when "ตั้งค่าตำแหน่ง" changes); before that
 * is ready, or for a place the routing service could not answer, a straight-line estimate is used. Every figure is
 * "ประมาณ" — never a promise of an arrival time.
 */

export const TRAVEL_TOKEN = '{ระยะทางมาสนาม}';

export interface TravelOrigin {
  /** The team this place belongs to (named in the message when the customer has several). */
  team?: string | null;
  province: string | null;
  district: string | null;
}

export interface TravelRow {
  province: string;
  /** '' = the province's centre. */
  district: string;
  distanceM: number;
  durationS: number;
  source: 'road' | 'estimate';
}

/** Straight line in metres (haversine). */
export function straightLineM(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const rad = Math.PI / 180;
  const dLat = (b.lat - a.lat) * rad;
  const dLng = (b.lng - a.lng) * rad;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin(dLng / 2) ** 2;
  return 2 * 6_371_000 * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Roads are longer than the straight line, and the average speed across Isan's highways and village roads is modest. */
const ROAD_FACTOR = 1.3;
const AVERAGE_KMH = 65;

export function estimateTravel(from: { lat: number; lng: number }, to: { lat: number; lng: number }): { distanceM: number; durationS: number } {
  const distanceM = Math.round(straightLineM(from, to) * ROAD_FACTOR);
  return { distanceM, durationS: Math.round((distanceM / 1000 / AVERAGE_KMH) * 3600) };
}

/** "85 กม." — whole kilometres (one decimal under 10 km). */
export function formatDistanceTh(m: number): string {
  const km = m / 1000;
  return `${km < 10 ? Math.max(0.1, Math.round(km * 10) / 10) : Math.round(km)} กม.`;
}

/** "1 ชม. 20 นาที" — rounded to 5 minutes (at least 5). */
export function formatDurationTh(s: number): string {
  const total = Math.max(5, Math.round(s / 300) * 5);
  const h = Math.floor(total / 60);
  const min = total % 60;
  return h ? (min ? `${h} ชม. ${min} นาที` : `${h} ชม.`) : `${min} นาที`;
}

const placeLabel = (province: string, district: string) => (district ? `อ.${district} จ.${province}` : `จ.${province}`);

/** The drive from one known district (null when the district is not one of ours, or the field is not set). */
export function travelOf(origin: TravelOrigin, rows: readonly TravelRow[], venue: VenueSetting | null | undefined): { place: string; distanceM: number; durationS: number } | null {
  if (!venue || venue.lat == null || venue.lng == null) return null;
  // Only a district we know (owner, 22 ต.ค. 2569: "ถ้าไม่พบในแผนที่ … ไม่ต้องแสดง" — a misspelt district shows nothing,
  // never a guess from the province's centre).
  const hit = findIsanDistrict(origin.district, origin.province);
  if (!hit) return null;
  const place = isanPlace(hit.province, hit.district)!;
  const stored = rows.find((r) => r.province === place.province && r.district === place.district);
  const t = stored ?? estimateTravel(place, { lat: venue.lat, lng: venue.lng });
  return { place: placeLabel(place.province, place.district), distanceM: t.distanceM, durationS: t.durationS };
}

/**
 * `{ระยะทางมาสนาม}` → (owner's layout, 22 ต.ค. 2569)
 *
 *   📍 ทีม ดาวเหนือ เดินทางมาสนามฟุตบอลหญ้าเทียมนาตาชู      ← "ทีม …" only when there are several teams
 *   จาก อ.เมืองขอนแก่น จ.ขอนแก่น
 *   • ระยะทางประมาณ 85 กม.
 *   • ขับรถประมาณ 1 ชม. 20 นาที
 *   🗺️ แผนที่: <Google Maps link of the field>
 *   เวลาเป็นค่าประมาณ ขึ้นกับการจราจร
 *
 * One block per place (teams from the same district share one). Nothing at all — the token and its empty line vanish —
 * when no team has a district we know or the field is not set.
 */
export function fillTravelToken(text: string, origins: readonly TravelOrigin[], rows: readonly TravelRow[], venue: VenueSetting | null | undefined): string {
  if (!text.includes(TRAVEL_TOKEN)) return text;
  const blocks = new Map<string, { teams: string[]; t: { place: string; distanceM: number; durationS: number } }>();
  for (const o of origins) {
    const t = travelOf(o, rows, venue);
    if (!t) continue;
    const b = blocks.get(t.place) ?? { teams: [], t };
    if (o.team && !b.teams.includes(o.team)) b.teams.push(o.team);
    blocks.set(t.place, b);
  }
  if (!blocks.size) return text.split(TRAVEL_TOKEN).join('').replace(/\n{3,}/g, '\n\n').trim();
  const teamCount = new Set(origins.map((o) => o.team).filter(Boolean)).size;
  const named = teamCount > 1;
  const where = venue!.name || 'สนาม';
  const body = [...blocks.values()].map(({ teams, t }) =>
    [
      `📍 ${named && teams.length ? `ทีม ${teams.join(', ')} ` : ''}เดินทางมา${where}`,
      `จาก ${t.place}`,
      `• ระยะทางประมาณ ${formatDistanceTh(t.distanceM)}`,
      `• ขับรถประมาณ ${formatDurationTh(t.durationS)}`,
    ].join('\n'),
  );
  const url = venueMapUrl(venue);
  const value = [body.join('\n\n'), ...(url ? [`🗺️ แผนที่: ${url}`] : []), 'เวลาเป็นค่าประมาณ ขึ้นกับการจราจร'].join('\n');
  return text.split(TRAVEL_TOKEN).join(value);
}
